<template>
  <div>
    <Navigation />
    <div class="wrap">
      <div class="head">
        <div>
          <h1>Reports</h1>
          <p class="lede">The numbers that actually run the business.</p>
        </div>
        <div class="head-actions">
          <button class="secondary" @click="exportCsv" :disabled="!currentRows.length">Export CSV</button>
        </div>
      </div>

      <div class="tabs">
        <button v-for="t in tabs" :key="t.key" :class="{tab: true, active: tab === t.key}" @click="switchTab(t.key)">
          <i :class="t.icon"></i> {{ t.label }}
        </button>
      </div>

      <!-- Date filter (shown on time-based reports) -->
      <div v-if="showDateFilter" class="filter-row card">
        <label>From</label>
        <input type="date" v-model="from" @change="load" />
        <label>To</label>
        <input type="date" v-model="to" @change="load" />
        <div class="filter-shortcuts">
          <button class="secondary small" @click="setRange(0)">Today</button>
          <button class="secondary small" @click="setRange(7)">7 days</button>
          <button class="secondary small" @click="setRange(30)">30 days</button>
          <button class="secondary small" @click="setRange(90)">90 days</button>
        </div>
      </div>

      <div v-if="loading" class="loader">Loading…</div>

      <!-- DAILY SALES -->
      <div v-else-if="tab === 'daily'" class="card">
        <div v-if="daily.totals" class="kpi-strip">
          <div class="kpi"><div class="kpi-label">Total sales</div><div class="kpi-val">KES {{ format(daily.totals.gross) }}</div></div>
          <div class="kpi"><div class="kpi-label">Transactions</div><div class="kpi-val">{{ daily.totals.n_sales }}</div></div>
          <div class="kpi"><div class="kpi-label">Discounts given</div><div class="kpi-val">KES {{ format(daily.totals.discount) }}</div></div>
          <div class="kpi"><div class="kpi-label">Avg ticket</div><div class="kpi-val">KES {{ format(daily.totals.n_sales > 0 ? daily.totals.gross / daily.totals.n_sales : 0) }}</div></div>
        </div>
        <table v-if="daily.days.length">
          <thead>
            <tr><th>Day</th><th class="r">Sales</th><th class="r">Gross</th><th class="r">Cash</th><th class="r">M-Pesa</th><th class="r">Card</th><th class="r">Discount</th></tr>
          </thead>
          <tbody>
            <tr v-for="d in daily.days" :key="d.day">
              <td>{{ d.day }}</td>
              <td class="r">{{ d.n_sales }}</td>
              <td class="r"><strong>KES {{ format(d.gross) }}</strong></td>
              <td class="r">KES {{ format(d.cash) }}</td>
              <td class="r">KES {{ format(d.mpesa) }}</td>
              <td class="r">KES {{ format(d.card) }}</td>
              <td class="r muted">KES {{ format(d.discount) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No sales in this period.</p>
      </div>

      <!-- TOP SELLERS -->
      <div v-else-if="tab === 'top'" class="card">
        <table v-if="top.length">
          <thead>
            <tr><th>#</th><th>Item</th><th>SKU</th><th class="r">Units</th><th class="r">Revenue</th><th class="r">Cost</th><th class="r">Margin</th></tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in top" :key="i">
              <td class="muted">{{ i + 1 }}</td>
              <td>{{ r.name }}</td>
              <td class="muted small">{{ r.sku }}</td>
              <td class="r"><strong>{{ Math.round(r.qty) }}</strong></td>
              <td class="r">KES {{ format(r.revenue) }}</td>
              <td class="r muted">KES {{ format(r.cost) }}</td>
              <td class="r" :class="{ ok: r.margin > 0 }">KES {{ format(r.margin) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No sales in this period.</p>
      </div>

      <!-- Z REPORT -->
      <div v-else-if="tab === 'z'" class="card">
        <div class="z-select">
          <label>Cash session:</label>
          <select v-model.number="zSessId" @change="load">
            <option :value="0" disabled>-- pick a session --</option>
            <option v-for="s in z.recent" :key="s.id" :value="s.id">
              #{{ s.id }} · {{ s.user_name }} · {{ s.shop_name }} · {{ s.status }} · opened {{ s.opened_at }}
            </option>
          </select>
        </div>
        <div v-if="z.session" class="z-report">
          <h3>Z-Report — Session #{{ z.session.id }}</h3>
          <div class="z-grid">
            <div><span>Cashier</span><strong>{{ z.session.user_name }}</strong></div>
            <div><span>Shop</span><strong>{{ z.session.shop_name }}</strong></div>
            <div><span>Status</span><strong>{{ z.session.status }}</strong></div>
            <div><span>Opened</span><strong>{{ z.session.opened_at }}</strong></div>
            <div><span>Closed</span><strong>{{ z.session.closed_at || '—' }}</strong></div>
          </div>
          <table>
            <tbody>
              <tr><td>Opening float</td><td class="r">KES {{ format(z.session.opening_float) }}</td></tr>
              <tr><td>+ Cash sales</td><td class="r">KES {{ format(z.session.cash_sales) }}</td></tr>
              <tr><td>− Cash refunds</td><td class="r">KES {{ format(z.session.cash_refunds) }}</td></tr>
              <tr><td>− Cash drops (safe)</td><td class="r">KES {{ format(z.session.cash_drops) }}</td></tr>
              <tr class="strong"><td>= Expected close</td><td class="r"><strong>KES {{ format(z.session.expected_close) }}</strong></td></tr>
              <tr v-if="z.session.counted_close !== null"><td>Counted close</td><td class="r">KES {{ format(z.session.counted_close) }}</td></tr>
              <tr v-if="z.session.variance !== null" class="strong">
                <td>Variance</td>
                <td class="r" :class="varianceClass(z.session.variance)">
                  <strong>{{ z.session.variance > 0 ? '+' : '' }}{{ format(z.session.variance) }}</strong>
                  <span class="var-label">{{ varianceLabel(z.session.variance) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="z.drops.length" style="margin-top: 20px;">
            <h4>Cash drops during shift</h4>
            <table>
              <thead><tr><th>When</th><th class="r">Amount</th><th>Reason</th></tr></thead>
              <tbody>
                <tr v-for="d in z.drops" :key="d.id">
                  <td class="muted">{{ d.created_at }}</td>
                  <td class="r">KES {{ format(d.amount) }}</td>
                  <td>{{ d.reason || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <p v-else class="muted center">Pick a session above to view its Z-report.</p>
      </div>

      <!-- DEAD STOCK -->
      <div v-else-if="tab === 'dead'" class="card">
        <div class="filter-inline">
          <label>Days unsold:</label>
          <select v-model.number="deadDays" @change="load">
            <option :value="30">30 days</option>
            <option :value="60">60 days</option>
            <option :value="90">90 days</option>
            <option :value="180">180 days</option>
            <option :value="365">1 year</option>
          </select>
          <span class="muted small" v-if="dead.total_value_at_cost">
            · Total tied-up value: <strong>KES {{ format(dead.total_value_at_cost) }}</strong>
          </span>
        </div>
        <table v-if="dead.rows && dead.rows.length">
          <thead>
            <tr><th>Product</th><th>Variant</th><th>Shop</th><th class="r">On hand</th><th class="r">Cost</th><th class="r">Tied up</th><th>Last sold</th></tr>
          </thead>
          <tbody>
            <tr v-for="r in dead.rows" :key="r.variant_id + '_' + r.shop_name">
              <td>{{ r.product_name }}</td>
              <td class="muted">{{ r.variant_name }} · {{ r.sku }}</td>
              <td>{{ r.shop_name }}</td>
              <td class="r"><strong>{{ r.on_hand }}</strong></td>
              <td class="r muted">KES {{ format(r.cost_price) }}</td>
              <td class="r warn">KES {{ format(r.value_at_cost) }}</td>
              <td class="muted small">{{ r.last_sold_at || 'Never' }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No dead stock over {{ deadDays }} days. Great inventory turn!</p>
      </div>

      <!-- MARGIN -->
      <div v-else-if="tab === 'margin'" class="card">
        <table v-if="margin.length">
          <thead>
            <tr><th>Product</th><th>Category</th><th>Brand</th><th class="r">Units</th><th class="r">Revenue</th><th class="r">Cost</th><th class="r">Margin</th><th class="r">%</th></tr>
          </thead>
          <tbody>
            <tr v-for="r in margin" :key="r.product_id">
              <td>{{ r.product_name }}</td>
              <td class="muted">{{ r.category_name || '—' }}</td>
              <td class="muted">{{ r.brand_name || '—' }}</td>
              <td class="r">{{ Math.round(r.qty) }}</td>
              <td class="r">KES {{ format(r.revenue) }}</td>
              <td class="r muted">KES {{ format(r.cost) }}</td>
              <td class="r"><strong>KES {{ format(r.gross_margin) }}</strong></td>
              <td class="r" :class="marginClass(r.margin_pct)">{{ r.margin_pct }}%</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No sales in this period.</p>
      </div>

      <!-- STOCK VALUATION -->
      <div v-else-if="tab === 'valuation'" class="card">
        <table v-if="valuation.length">
          <thead>
            <tr><th>Shop</th><th class="r">Distinct SKUs</th><th class="r">Total units</th><th class="r">At cost</th><th class="r">At retail</th><th class="r">Potential margin</th></tr>
          </thead>
          <tbody>
            <tr v-for="r in valuation" :key="r.shop_id">
              <td><strong>{{ r.shop_name }}</strong></td>
              <td class="r">{{ r.distinct_skus }}</td>
              <td class="r">{{ r.total_units }}</td>
              <td class="r">KES {{ format(r.value_at_cost) }}</td>
              <td class="r">KES {{ format(r.value_at_retail) }}</td>
              <td class="r ok">KES {{ format(r.value_at_retail - r.value_at_cost) }}</td>
            </tr>
            <tr class="strong">
              <td><strong>All shops</strong></td>
              <td class="r"><strong>—</strong></td>
              <td class="r"><strong>{{ valuationTotals.units }}</strong></td>
              <td class="r"><strong>KES {{ format(valuationTotals.cost) }}</strong></td>
              <td class="r"><strong>KES {{ format(valuationTotals.retail) }}</strong></td>
              <td class="r ok"><strong>KES {{ format(valuationTotals.retail - valuationTotals.cost) }}</strong></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No stock records.</p>
      </div>

      <!-- COMMISSIONS -->
      <div v-else-if="tab === 'commissions'" class="card">
        <h3 style="margin-bottom:8px;">By provider</h3>
        <table v-if="commissions.by_provider.length">
          <thead>
            <tr><th>Provider</th><th class="r">Services done</th><th class="r">Gross service revenue</th><th class="r">Commission earned</th><th class="r">Unpaid</th></tr>
          </thead>
          <tbody>
            <tr v-for="r in commissions.by_provider" :key="r.provider_user_id">
              <td><strong>{{ r.provider_name || '—' }}</strong></td>
              <td class="r">{{ r.services_done }}</td>
              <td class="r">KES {{ format(r.gross_service_revenue) }}</td>
              <td class="r"><strong>KES {{ format(r.commission_earned) }}</strong></td>
              <td class="r warn">KES {{ format(r.commission_unpaid) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No commissions recorded in this period.</p>

        <h3 v-if="commissions.ledger.length" style="margin: 20px 0 8px;">Ledger (last 200)</h3>
        <table v-if="commissions.ledger.length">
          <thead>
            <tr><th>When</th><th>Provider</th><th>Service</th><th class="r">Price</th><th class="r">%</th><th class="r">Earned</th><th>Paid out?</th></tr>
          </thead>
          <tbody>
            <tr v-for="l in commissions.ledger" :key="l.id">
              <td class="muted small">{{ l.created_at }}</td>
              <td>{{ l.provider_name }}</td>
              <td>{{ l.service_name }}</td>
              <td class="r">KES {{ format(l.service_price) }}</td>
              <td class="r">{{ l.commission_percent }}%</td>
              <td class="r"><strong>KES {{ format(l.commission_amount) }}</strong></td>
              <td class="muted small">{{ l.paid_out_at || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- DISCOUNTS -->
      <div v-else-if="tab === 'discounts'" class="card">
        <div v-if="discountsRep.totals" class="kpi-strip">
          <div class="kpi">
            <div class="kpi-label">Total discounts given</div>
            <div class="kpi-val warn">KES {{ format(discountsRep.totals.total_discount) }}</div>
            <div class="kpi-sub">across {{ discountsRep.totals.sales_with_discount }} sale(s)</div>
          </div>
          <div class="kpi">
            <div class="kpi-label">Via codes</div>
            <div class="kpi-val">KES {{ format(codeTotalDiscount) }}</div>
          </div>
          <div class="kpi">
            <div class="kpi-label">Ad-hoc (no code)</div>
            <div class="kpi-val">KES {{ format(discountsRep.adhoc ? discountsRep.adhoc.total_discount : 0) }}</div>
            <div class="kpi-sub">{{ discountsRep.adhoc ? discountsRep.adhoc.uses : 0 }} sale(s)</div>
          </div>
        </div>

        <h3 style="margin-top:16px;">By code</h3>
        <table v-if="discountsRep.by_code.length">
          <thead>
            <tr><th>Code</th><th>Description</th><th class="r">Type</th><th class="r">Uses</th><th class="r">Subtotal sold</th><th class="r">Discount given</th><th class="r">Charged</th></tr>
          </thead>
          <tbody>
            <tr v-for="c in discountsRep.by_code" :key="c.id">
              <td><code>{{ c.code }}</code></td>
              <td class="muted small">{{ c.description || '—' }}</td>
              <td class="r muted">{{ c.discount_type === 'percent' ? c.value + '%' : 'KES ' + format(c.value) }}</td>
              <td class="r">{{ c.uses }}</td>
              <td class="r">KES {{ format(c.total_subtotal) }}</td>
              <td class="r warn">KES {{ format(c.total_discount) }}</td>
              <td class="r"><strong>KES {{ format(c.total_charged) }}</strong></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No codes tracked in this period.</p>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute } from '@/api';

export default {
  name: 'Reports',
  components: { Navigation },
  data() {
    return {
      tab: 'daily',
      tabs: [
        { key: 'daily',     label: 'Daily Sales',    icon: 'mdi mdi-chart-timeline-variant' },
        { key: 'top',       label: 'Top Sellers',    icon: 'mdi mdi-trending-up' },
        { key: 'z',         label: 'Z-Report',       icon: 'mdi mdi-cash-register' },
        { key: 'dead',      label: 'Dead Stock',     icon: 'mdi mdi-package-variant-closed-remove' },
        { key: 'margin',    label: 'Margin',         icon: 'mdi mdi-scale-balance' },
        { key: 'valuation', label: 'Stock Valuation',icon: 'mdi mdi-currency-usd' },
        { key: 'commissions', label: 'Commissions',  icon: 'mdi mdi-account-cash' },
        { key: 'discounts',  label: 'Discount Usage', icon: 'mdi mdi-tag-outline' },
      ],
      from: this.defaultFrom(),
      to: this.defaultTo(),
      loading: false,
      daily: { days: [], totals: null },
      top: [],
      z: { session: null, drops: [], recent: [] },
      zSessId: 0,
      dead: { rows: [], total_value_at_cost: 0 },
      deadDays: 90,
      margin: [],
      valuation: [],
      commissions: { by_provider: [], ledger: [] },
      discountsRep: { by_code: [], adhoc: null, totals: null },
    };
  },
  computed: {
    showDateFilter() { return ['daily', 'top', 'margin', 'commissions', 'discounts'].includes(this.tab); },
    currentRows() {
      if (this.tab === 'daily')     return this.daily.days;
      if (this.tab === 'top')       return this.top;
      if (this.tab === 'dead')      return this.dead.rows;
      if (this.tab === 'margin')    return this.margin;
      if (this.tab === 'valuation') return this.valuation;
      if (this.tab === 'commissions') return this.commissions.by_provider;
      if (this.tab === 'discounts')   return this.discountsRep.by_code;
      if (this.tab === 'z')         return this.z.session ? [this.z.session] : [];
      return [];
    },
    valuationTotals() {
      return this.valuation.reduce((a, r) => ({
        units: a.units + Number(r.total_units || 0),
        cost:  a.cost  + Number(r.value_at_cost || 0),
        retail:a.retail+ Number(r.value_at_retail || 0),
      }), { units: 0, cost: 0, retail: 0 });
    },
    codeTotalDiscount() {
      return this.discountsRep.by_code.reduce((s, c) => s + Number(c.total_discount || 0), 0);
    },
  },
  async mounted() { await this.load(); },
  methods: {
    defaultFrom() { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); },
    defaultTo() { return new Date().toISOString().slice(0, 10); },
    setRange(days) {
      this.to = this.defaultTo();
      const d = new Date(); d.setDate(d.getDate() - days);
      this.from = d.toISOString().slice(0, 10);
      this.load();
    },
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    varianceClass(v) { const n = Number(v); if (n === 0) return 'ok'; if (n < 0) return 'warn'; return 'muted'; },
    varianceLabel(v) { const n = Number(v); if (n === 0) return '(exact)'; if (n > 0) return '(over)'; return '(short)'; },
    marginClass(pct) { if (pct >= 40) return 'ok'; if (pct >= 15) return ''; return 'warn'; },
    async switchTab(t) { this.tab = t; await this.load(); },
    async load() {
      this.loading = true;
      try {
        if (this.tab === 'daily') {
          const { data } = await execute({ function: 'reportDailySales', from: this.from, to: this.to }, paysol);
          if (data.success) this.daily = data.data;
        } else if (this.tab === 'top') {
          const { data } = await execute({ function: 'reportTopSellers', from: this.from, to: this.to }, paysol);
          if (data.success) this.top = data.data;
        } else if (this.tab === 'z') {
          const { data } = await execute({ function: 'reportZReport', id: this.zSessId }, paysol);
          if (data.success) this.z = data.data;
        } else if (this.tab === 'dead') {
          const { data } = await execute({ function: 'reportDeadStock', days: this.deadDays }, paysol);
          if (data.success) this.dead = data.data;
        } else if (this.tab === 'margin') {
          const { data } = await execute({ function: 'reportMargin', from: this.from, to: this.to }, paysol);
          if (data.success) this.margin = data.data;
        } else if (this.tab === 'valuation') {
          const { data } = await execute({ function: 'reportStockValuation' }, paysol);
          if (data.success) this.valuation = data.data;
        } else if (this.tab === 'commissions') {
          const { data } = await execute({ function: 'reportCommissions', from: this.from, to: this.to }, paysol);
          if (data.success) this.commissions = data.data;
        } else if (this.tab === 'discounts') {
          const { data } = await execute({ function: 'reportDiscountUsage', from: this.from, to: this.to }, paysol);
          if (data.success) this.discountsRep = data.data;
        }
      } finally { this.loading = false; }
    },
    exportCsv() {
      const rows = this.currentRows;
      if (!rows.length) return;
      const cols = Object.keys(rows[0]);
      const csv = [cols.join(',')].concat(rows.map(r => cols.map(c => {
        const v = r[c];
        if (v == null) return '';
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      }).join(','))).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `paysol-${this.tab}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  }
};
</script>

<style scoped>
.wrap { max-width: 1300px; margin: 0 auto; padding: 24px; }
.head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 20px; gap: 20px; }
h1 { font-size: 26px; margin: 0 0 4px; color: var(--paysol-text); }
.lede { color: var(--paysol-muted); margin: 0; }
.tabs { display: flex; gap: 4px; overflow-x: auto; padding-bottom: 8px; margin-bottom: 16px; border-bottom: 1px solid var(--paysol-line); }
button.tab { background: transparent; color: var(--paysol-muted); padding: 10px 14px; border-radius: 6px 6px 0 0; font-size: 13px; display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; border-bottom: 2px solid transparent; }
button.tab:hover { color: var(--paysol-text); }
button.tab.active { color: var(--paysol-teal); border-bottom-color: var(--paysol-teal); background: var(--paysol-teal-glow); }
button.tab i { font-size: 16px; }
.filter-row { display: flex; align-items: end; gap: 12px; margin-bottom: 16px; padding: 14px; flex-wrap: wrap; }
.filter-row label { margin: 0; }
.filter-row input { width: auto; max-width: 180px; }
.filter-shortcuts { display: flex; gap: 4px; margin-left: auto; }
.filter-inline { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; padding: 10px 4px; }
.filter-inline label { margin: 0; }
.filter-inline select { width: auto; }
button.small { padding: 6px 10px; font-size: 11px; }
.loader { color: var(--paysol-muted); padding: 20px; }
.kpi-strip { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; margin-bottom: 20px; }
.kpi { padding: 14px; background: var(--paysol-bg-elev); border-radius: 8px; border-left: 3px solid var(--paysol-teal); }
.kpi-label { color: var(--paysol-muted); font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
.kpi-val { color: var(--paysol-text); font-size: 20px; font-weight: 700; margin-top: 4px; }
.r { text-align: right; }
.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.ok { color: var(--paysol-success); }
.warn { color: var(--paysol-warning); }
.center { text-align: center; padding: 40px 0; }
tr.strong td { background: var(--paysol-bg-elev); border-top: 1px solid var(--paysol-teal); }
.z-select { margin-bottom: 20px; }
.z-select select { max-width: 100%; }
.z-report h3 { color: var(--paysol-text); margin: 0 0 14px; font-size: 18px; }
.z-report h4 { color: var(--paysol-muted); font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 8px; }
.z-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; margin-bottom: 16px; padding: 12px; background: var(--paysol-bg-elev); border-radius: 6px; }
.z-grid div { display: flex; flex-direction: column; gap: 3px; }
.z-grid span { color: var(--paysol-muted); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
.z-grid strong { color: var(--paysol-text); font-size: 13px; }
.var-label { color: var(--paysol-muted); font-weight: 500; margin-left: 4px; font-size: 11px; }
</style>
